import * as React from 'react'
import type { JSONContent } from '@tiptap/core'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { patchJSON } from '@/lib/api'

interface EditableBlast {
  id: number
  lock_version: number
  subject: string
  body_document: JSONContent
  audience?: {
    segments: { id: number }[]
    groups: { id: number }[]
    contacts: { id: number }[]
  }
}

interface AudiencePayload {
  segment_ids: number[]
  group_ids: number[]
  contact_ids: number[]
}

interface AutosaveContents {
  subject: string
  bodyDocument: JSONContent
  audience: AudiencePayload
}

export function useBlastDraftAutosave<T extends EditableBlast>({
  blast,
  subject,
  bodyDocument,
  audience,
}: {
  blast: T | null
  subject: string
  bodyDocument: JSONContent
  audience: AudiencePayload
}) {
  const queryClient = useQueryClient()
  const revision = React.useRef(blast?.lock_version ?? 0)
  const initialized = React.useRef(blast === null)
  const lastQueuedSignature = React.useRef(blast ? blastSignature(blast) : null)
  const autosave = useMutation({
    scope: { id: `blast-${blast?.id ?? 'new'}-autosave` },
    mutationFn: (contents: AutosaveContents) =>
      patchJSON<{ blast: T }>(`/api/v1/blasts/${blast?.id}`, {
        blast: {
          subject: contents.subject,
          body_document: contents.bodyDocument,
          ...contents.audience,
          lock_version: revision.current,
        },
      }),
    onSuccess: ({ blast: saved }) => {
      revision.current = saved.lock_version
      queryClient.setQueryData<{ blasts: T[] }>(['blasts'], (current) => ({
        blasts: (current?.blasts ?? []).map((row) => (row.id === saved.id ? saved : row)),
      }))
    },
  })
  const { mutate, mutateAsync } = autosave

  React.useEffect(() => {
    if (!blast) return
    const signature = draftSignature(subject, bodyDocument, audience)
    if (!initialized.current) {
      initialized.current = signature === blastSignature(blast)
      return
    }
    if (signature === lastQueuedSignature.current) return

    lastQueuedSignature.current = signature

    const timeout = window.setTimeout(() => mutate({ subject, bodyDocument, audience }), 400)
    return () => window.clearTimeout(timeout)
  }, [subject, bodyDocument, audience, blast, mutate])

  return {
    ...autosave,
    flushThen: (callback: () => void) => {
      void mutateAsync({ subject, bodyDocument, audience }).then(callback).catch(ignoreSaveError)
    },
  }
}

function blastSignature(blast: EditableBlast) {
  const audience = blast.audience
  return draftSignature(blast.subject, blast.body_document, {
    segment_ids: audience?.segments.map(({ id }) => id) ?? [],
    group_ids: audience?.groups.map(({ id }) => id) ?? [],
    contact_ids: audience?.contacts.map(({ id }) => id) ?? [],
  })
}

function draftSignature(subject: string, bodyDocument: JSONContent, audience: AudiencePayload) {
  return JSON.stringify([subject, bodyDocument, audience])
}

function ignoreSaveError() {
  // The mutation exposes its error to the composer, which stays open.
}

export function blastComposerStatus(
  existing: boolean,
  autosave: { isPending: boolean; isSuccess: boolean },
) {
  if (!existing) return 'Write a single-column announcement inside your email branding.'
  if (autosave.isPending) return 'Autosaving…'
  if (autosave.isSuccess) return 'Autosaved just now'
  return 'Draft changes save automatically'
}
